import type { AnchorPlacement } from '../primitives/AppPopover/use-anchored-position'
import type { CoachmarkVariant } from './AppCoachmark/coachmark-types'

/** One anchored explanation: the element it points at, its copy keys, and where the card sits. */
export interface TourStep {
  /** Key an element registers itself under with `v-coach`. */
  targetKey: string
  titleKey: string
  bodyKey: string
  /** Side of the target the card sits on; defaults to the coachmark's own default. */
  placement?: AnchorPlacement
}

/** A tour as data: an id, a treatment, an optional route it belongs to, and its steps in order. */
export interface TourDefinition {
  id: string
  variant: CoachmarkVariant
  labelKey: string
  /** Route name this tour explains, for a caller that has to navigate before starting it. */
  route?: string
  steps: readonly TourStep[]
}

export const BASICS_TOUR_ID = 'basics'

const BASICS_TOUR: TourDefinition = {
  id: BASICS_TOUR_ID,
  variant: 'spotlight',
  labelKey: 'tour.hub.tours.basics',
  route: 'home',
  steps: [
    {
      targetKey: 'home.composer',
      titleKey: 'tour.basics.composer.title',
      bodyKey: 'tour.basics.composer.body',
      placement: 'top',
    },
    {
      targetKey: 'shell.nav',
      titleKey: 'tour.basics.nav.title',
      bodyKey: 'tour.basics.nav.body',
      placement: 'right-start',
    },
    {
      targetKey: 'shell.dockButton',
      titleKey: 'tour.basics.dock.title',
      bodyKey: 'tour.basics.dock.body',
      placement: 'left-end',
    },
    {
      targetKey: 'shell.tourHub',
      titleKey: 'tour.basics.hub.title',
      bodyKey: 'tour.basics.hub.body',
      placement: 'right-end',
    },
  ],
}

const CONTENT_TOUR: TourDefinition = {
  id: 'content',
  variant: 'soft',
  labelKey: 'tour.hub.tours.content',
  route: 'calendar',
  steps: [
    {
      targetKey: 'content.board',
      titleKey: 'tour.content.board.title',
      bodyKey: 'tour.content.board.body',
      placement: 'top',
    },
    {
      targetKey: 'content.openSlot',
      titleKey: 'tour.content.plan.title',
      bodyKey: 'tour.content.plan.body',
      placement: 'bottom-end',
    },
  ],
}

const BRAND_TOUR: TourDefinition = {
  id: 'brand',
  variant: 'soft',
  labelKey: 'tour.hub.tours.brand',
  route: 'identity-brand-facts',
  steps: [
    {
      targetKey: 'brand.foundation',
      titleKey: 'tour.brand.foundation.title',
      bodyKey: 'tour.brand.foundation.body',
      placement: 'right-start',
    },
    {
      targetKey: 'brand.style',
      titleKey: 'tour.brand.style.title',
      bodyKey: 'tour.brand.style.body',
      placement: 'right-start',
    },
  ],
}

export const TOURS: Readonly<Record<string, TourDefinition>> = {
  [BASICS_TOUR_ID]: BASICS_TOUR,
  [CONTENT_TOUR.id]: CONTENT_TOUR,
  [BRAND_TOUR.id]: BRAND_TOUR,
}

export function tourById(id: string): TourDefinition | undefined {
  return TOURS[id]
}
